import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import type { DeathCorrectionView, DeathRecordView } from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  LoadingBlock,
  Notice,
  PageHeader,
  Row,
  Section,
  Table,
  TableBody,
  TableHeader,
  Tag,
} from '@wodi/ui';
import { useState } from 'react';

import { can, sessionQuery } from '../api/session.ts';
import { deathQuery, decideDeathCorrection, requestDeathCorrection } from '../api/vital-events.ts';
import { useOnline } from '../hooks/use-online.ts';
import { TableScroll } from '../layout/screen.tsx';
import { PatientBannerLoader } from '../patients/patient-banner.tsx';
import { formatInstant } from '../patients/format.ts';
import { ReasonDialog } from '../patients/reason-dialog.tsx';
import { toasts } from '../toasts.ts';
import { DeathSummaryPrinter } from './prints.tsx';
import styles from './vital-events.module.css';
import {
  CASE_STATUS_TAGS,
  CAUSE_ROLE_WORDS,
  DEATH_STATUS_TAGS,
  errorText,
  MANNER_WORDS,
  PLACE_WORDS,
  REQUEST_TAGS,
} from './words.ts';

// One death record (docs/plans/phase2-mortuary-and-notifications.md §6): the facts, the cause
// chain, corrections (entered in error with a second person approving), the printed summary
// and the mortuary case.

const route = getRouteApi('/_app/deaths/$deathId');

function Corrections({ death }: { death: DeathRecordView }) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const online = useOnline();
  const queryClient = useQueryClient();
  const [asking, setAsking] = useState(false);
  const [rejecting, setRejecting] = useState<DeathCorrectionView | null>(null);
  const update = (view: DeathRecordView) => {
    queryClient.setQueryData(deathQuery(death.id).queryKey, view);
    void queryClient.invalidateQueries({ queryKey: ['patients'] });
  };
  const ask = useMutation({
    mutationFn: (reason: string) => requestDeathCorrection(death.id, reason),
    onSuccess: (view) => {
      update(view);
      setAsking(false);
      toasts.show({ title: 'Correction requested' });
    },
  });
  const decide = useMutation({
    mutationFn: (input: { id: string; action: 'approve' | 'reject' | 'withdraw'; note?: string }) =>
      decideDeathCorrection(input.id, input.action, input.note),
    onSuccess: (view, input) => {
      update(view);
      setRejecting(null);
      toasts.show({
        title:
          input.action === 'approve'
            ? 'Death record marked entered in error'
            : input.action === 'reject'
              ? 'Correction rejected'
              : 'Correction withdrawn',
      });
    },
  });
  const me = session?.user.id;
  const mayAsk = session !== null && can(session, 'deaths.correct.request');
  const mayApprove = session !== null && can(session, 'deaths.correct.approve');
  const open = death.corrections.find((c) => c.status === 'requested');
  return (
    <Section title="Corrections">
      {death.corrections.length === 0 ? (
        <p className={styles.meta}>
          No corrections. If this death was recorded for the wrong person or in error, ask for it to
          be marked entered in error; a second person approves.
        </p>
      ) : (
        <ul className={styles.list} aria-label="Corrections">
          {death.corrections.map((c) => {
            const [word, shape] = REQUEST_TAGS[c.status];
            return (
              <li key={c.id} className={styles.listRow}>
                <span>
                  {`${c.reason} (asked by ${c.requestedBy.displayName}, ${formatInstant(c.requestedAt)})`}
                  {c.decisionNote === null ? null : (
                    <span className={styles.meta}>{` Note: ${c.decisionNote}`}</span>
                  )}
                </span>
                <Tag state={shape}>{word}</Tag>
              </li>
            );
          })}
        </ul>
      )}
      {decide.error === null ? null : (
        <Notice tone="error">{errorText(decide.error, 'That did not work. Try again.')}</Notice>
      )}
      <div className={styles.actions}>
        {death.status === 'recorded' && open === undefined && mayAsk ? (
          <Button
            isDisabled={!online}
            onPress={() => {
              setAsking(true);
            }}
          >
            Ask to mark entered in error
          </Button>
        ) : null}
        {open !== undefined && mayApprove && open.requestedBy.id !== me ? (
          <>
            <Button
              variant="primary"
              isDisabled={!online}
              isPending={decide.isPending}
              pendingLabel="Approving…"
              onPress={() => {
                decide.mutate({ id: open.id, action: 'approve' });
              }}
            >
              Approve correction
            </Button>
            <Button
              isDisabled={!online}
              onPress={() => {
                setRejecting(open);
              }}
            >
              Reject
            </Button>
          </>
        ) : null}
        {open !== undefined && open.requestedBy.id === me ? (
          <Button
            variant="quiet"
            isDisabled={!online}
            onPress={() => {
              decide.mutate({ id: open.id, action: 'withdraw' });
            }}
          >
            Withdraw
          </Button>
        ) : null}
      </div>
      <ReasonDialog
        title="Ask to mark entered in error"
        isOpen={asking}
        onOpenChange={setAsking}
        confirmLabel="Ask to mark entered in error"
        pendingLabel="Asking…"
        isPending={ask.isPending}
        error={ask.error === null ? null : errorText(ask.error, 'The request was not sent.')}
        onConfirm={(reason) => {
          ask.mutate(reason);
        }}
      />
      <ReasonDialog
        title="Reject the correction"
        isOpen={rejecting !== null}
        onOpenChange={(o) => {
          if (!o) setRejecting(null);
        }}
        confirmLabel="Reject"
        pendingLabel="Rejecting…"
        isPending={decide.isPending}
        error={null}
        onConfirm={(note) => {
          if (rejecting !== null) decide.mutate({ id: rejecting.id, action: 'reject', note });
        }}
      />
    </Section>
  );
}

export function DeathPage() {
  const { deathId } = route.useParams();
  const { data: session } = useSuspenseQuery(sessionQuery);
  const death = useQuery(deathQuery(deathId));
  const [printing, setPrinting] = useState(false);
  if (death.isPending) return <LoadingBlock label="Loading the death record" rows={6} />;
  if (death.isError) {
    return (
      <Notice tone="error" action={<Button onPress={() => void death.refetch()}>Try again</Button>}>
        {errorText(death.error, "The death record couldn't be loaded. Try again.")}
      </Notice>
    );
  }
  const d = death.data;
  const [word, shape] = DEATH_STATUS_TAGS[d.status];
  const certifiedHere = d.place !== 'outside_facility';
  const mayIntake = session !== null && can(session, 'mortuary.intake');
  const mayReadMortuary = session !== null && can(session, 'mortuary.read');
  const mayRecord = session !== null && can(session, 'deaths.record');
  return (
    <div className={styles.page}>
      <PatientBannerLoader patientId={d.patientId} />
      <PageHeader
        title="Death record"
        description={`Recorded ${formatInstant(d.recordedAt)} by ${d.recordedBy.displayName}.`}
        actions={
          d.status === 'recorded' && certifiedHere ? (
            <Button
              onPress={() => {
                setPrinting((p) => !p);
              }}
            >
              {printing ? 'Hide summary' : 'Print notification summary'}
            </Button>
          ) : undefined
        }
      />
      {d.status === 'entered_in_error' && d.enteredInError !== null ? (
        <Notice tone="info">
          {`Entered in error on ${formatInstant(d.enteredInError.at)}: ${d.enteredInError.reason}. Asked by ${d.enteredInError.requestedBy.displayName}, approved by ${d.enteredInError.approvedBy.displayName}.`}
        </Notice>
      ) : null}
      {d.supersedesId === null ? null : (
        <Notice tone="info">
          This record replaces one entered in error.{' '}
          <Link to="/deaths/$deathId" params={{ deathId: d.supersedesId }}>
            Open the earlier record
          </Link>
        </Notice>
      )}
      {d.status === 'entered_in_error' && d.place === 'ward' && mayRecord ? (
        <p className={styles.actions}>
          <Link to="/visits/$visitId/record-death" params={{ visitId: d.visitId }}>
            Record death again
          </Link>
        </p>
      ) : null}
      {printing ? <DeathSummaryPrinter deathId={d.id} /> : null}
      <Section title="Death" actions={<Tag state={shape}>{word}</Tag>}>
        <dl className={styles.facts}>
          <dt>Date and time</dt>
          <dd>{formatInstant(d.diedAt)}</dd>
          <dt>Place</dt>
          <dd>{PLACE_WORDS[d.place]}</dd>
          <dt>Facility</dt>
          <dd>{d.facility.name}</dd>
          <dt>Certifying clinician</dt>
          <dd>{d.certifiedBy?.displayName ?? 'Certified elsewhere'}</dd>
          <dt>Manner</dt>
          <dd>{d.manner === null ? 'Not recorded here' : MANNER_WORDS[d.manner]}</dd>
          {d.notes === null ? null : (
            <>
              <dt>Notes</dt>
              <dd className={styles.prose}>{d.notes}</dd>
            </>
          )}
        </dl>
      </Section>
      <Section title="Cause of death">
        {d.causes.length === 0 ? (
          <p className={styles.meta}>
            {certifiedHere
              ? 'No cause recorded yet: pending investigation.'
              : 'Certified elsewhere.'}
          </p>
        ) : (
          <TableScroll>
            <Table aria-label="Cause of death" flat>
              <TableHeader>
                <Column isRowHeader>Part</Column>
                <Column>ICD-11</Column>
                <Column>Condition</Column>
                <Column>Time from onset</Column>
              </TableHeader>
              <TableBody items={d.causes}>
                {(c) => (
                  <Row id={c.id}>
                    <Cell>{CAUSE_ROLE_WORDS[c.role]}</Cell>
                    <Cell>
                      {c.code === null ? 'Not coded' : <span className="wodi-id">{c.code}</span>}
                    </Cell>
                    <Cell>{[c.display, c.clinicianText].filter((x) => x !== null).join('. ')}</Cell>
                    <Cell>{c.onsetInterval ?? ''}</Cell>
                  </Row>
                )}
              </TableBody>
            </Table>
          </TableScroll>
        )}
      </Section>
      <Section title="Mortuary">
        {d.mortuaryCase !== null ? (
          <p className={styles.actions}>
            {mayReadMortuary ? (
              <Link to="/mortuary/cases/$caseId" params={{ caseId: d.mortuaryCase.id }}>
                {`Mortuary case ${d.mortuaryCase.caseNumber}`}
              </Link>
            ) : (
              <span className="wodi-id">{d.mortuaryCase.caseNumber}</span>
            )}
            <Tag state={CASE_STATUS_TAGS[d.mortuaryCase.status][1]}>
              {CASE_STATUS_TAGS[d.mortuaryCase.status][0]}
            </Tag>
          </p>
        ) : d.status === 'recorded' && mayIntake ? (
          <Link to="/mortuary/receive" search={{ deathId: d.id }}>
            Receive into mortuary
          </Link>
        ) : (
          <p className={styles.meta}>Not received into the mortuary.</p>
        )}
      </Section>
      <Corrections death={d} />
    </div>
  );
}
